import { Persistable } from "../../nr-scripting-runtime/api/Persistable";
import { AGENT_EVENTS, gracefulChunkingWithLimit } from "../utils/agent-utils";
import { sanitizeAIMessage } from "../utils/chat-ai-message";
import { getAuthHeaders } from "../utils/getToken";
import {
  activateAgentPeer as activateAgentPeerRequest,
  createThread,
  sendAudioTranscription,
  OPENAI_AGENT_URL,
  PROVIDERS
} from "../utils/openAI-utils";
import AudioManager from "./Agents/AudioManagerAI";
import voiceSynthesizer from "./VoiceSynthetizer";
import { DIALOG_CONNECTION_CONNECTED } from "../naf-dialog-adapter";
import {
  DEFAULT_PERSISTABLE_ENV,
  buildAgentPeerActivationPayload,
  buildAgentPrivateChatPayload,
  getAgentPeerActivationKey,
  getAgentPeerId
} from "../utils/agent-peer-id";
import { resolveRoomUserId } from "../utils/room-persistence-utils";
import {
  clearAgentPeerResponseWatchdog,
  markAgentPeerResponseReceived,
  startAgentPeerResponseWatchdog
} from "../utils/agent-peer-response-watchdog";
import { AgentChatAudioCache } from "../utils/agent-chat-audio-cache";

let isCancelled = false;

export type Agent = {
  apiKey: string;
  assistantId?: string;
  model?: string;
  endpoint?: string;
  inputType: string;
  encrypted: boolean;
  enableOnProximity: boolean;
  proximityRadius: number;
  name: string;
  isAi?: boolean;
  id: string;
  objectUuid: string;
  el?: any;
  threadId?: string;
  welcomeText?: string;
  voiceSpeechStrategy?: "OpenAI" | "WebAPI" | "Silent";
  audioBuffers: any;
  welcomeTextUrl?: string;
  currentAudio: HTMLAudioElement;
  provider?: string;
  systemPrompt?: string;
  currentPromptId?: number;
  persistable?: string;
  persistableInstance?: Persistable;
  agentPeerId?: string;
  agentPeerStatus?: "pending" | "active" | "disabled" | "error";
  agentPeerActivationKey?: string;
  agentPeerActivationPromise?: Promise<void>;
  chatHistory: { role: string; content: string; timestamp: number }[];
  targetLanguages?: string[];
  dataSources?: string[];
  enableTranslation?: boolean;
};

type PendingPrivateChatPrompt = {
  agent: Agent;
  messageId?: any;
  accumulatedText: string;
  hasAgentResponse?: boolean;
  agentPeerResponseTimedOut?: boolean;
  agentPeerResponseTimeoutId?: ReturnType<typeof setTimeout>;
  onMessageChunk: (chunk: { type: string; data: string }) => Promise<void>;
  onCompletedText: (text: string) => Promise<void>;
  onEnd: () => Promise<void>;
  onError: (error: string) => void;
};

export const MEMORY_TYPES = {
  "temporary": "temporary",
  "persistent": "persistent"
}

const AGENT_PEER_CHAT_PROVIDERS = new Set([
  "OpenAI",
  PROVIDERS.OPEN_AI_ASSISTANT,
  PROVIDERS.OPEN_AI_COMPLETION
]);

function debugPrivateChatStream(stage: string, payload: Record<string, any>): void {
  try {
    if (window.localStorage?.getItem("debugAIPrivateChatStream") !== "true") {
      return;
    }
  } catch (error) {
    return;
  }

  const printable = Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [key, typeof value === "string" ? debugString(value) : value])
  );
  console.debug("[AI private chat stream]", stage, printable);
}

function debugString(value: string): Record<string, any> {
  const chars = Array.from(value);

  return {
    raw: value,
    visible: value.replace(/ /g, "·").replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t"),
    length: value.length,
    headCodes: chars.slice(0, 12).map(char => char.charCodeAt(0)),
    tailCodes: chars.slice(-12).map(char => char.charCodeAt(0))
  };
}

function getAgentChatAudioMessageId(message: any): string | null {
  const clientMessageId = message?.body?.clientMessageId || message?.clientMessageId;
  const messageId = clientMessageId || message?.id;

  return typeof messageId === "string" && messageId ? messageId : null;
}

function getAgentChatAudioText(message: any): string {
  const body = message?.body;
  const text = typeof body === "string" ? body : body?.message;

  return typeof text === "string" ? text.trim() : "";
}

class AIAgents {
  private static instance: AIAgents;
  public status: "talking" | "listening" | "idle" | "thinking";
  public currentPromptId?: number;
  private agents: Array<Agent>;
  private errorOpenAIAudio: string;
  private audioManager = new AudioManager();
  private agentChatAudioCache = new AgentChatAudioCache();
  private bufferKey = 0;
  private boundDialog: any = null;
  private dialogEventHandlers: {
    started: (data: any) => void;
    delta: (data: any) => void;
    completed: (data: any) => void;
    error: (data: any) => void;
    connected: () => void;
  } | null = null;
  private pendingPrivateChatPrompts = new Map<string, PendingPrivateChatPrompt>();

  private currentAbortController: AbortController | null = null;


  private constructor() {
    this.agents = [];
    this.status = "idle";
    this.errorOpenAIAudio = "https://nextrealitydigital.blob.core.windows.net/nrd/metaverse/error-handler-npc.mp3";
  }

  public static getInstance(): AIAgents {
    if (!AIAgents.instance) {
      AIAgents.instance = new AIAgents();
    }

    return AIAgents.instance;
  }

  private bindDialogPrivateChatEvents(): void {
    const dialog = window.APP?.dialog;

    if (!dialog || this.boundDialog === dialog) {
      return;
    }

    this.unbindDialogPrivateChatEvents();

    const handlers = {
      started: (data: any) => {
        this.handlePrivateChatMessageStarted(data);
      },
      delta: (data: any) => {
        void this.handlePrivateChatMessageDelta(data);
      },
      completed: (data: any) => {
        void this.handlePrivateChatMessageCompleted(data);
      },
      error: (data: any) => {
        this.handlePrivateChatMessageError(data);
      },
      connected: () => {
        this.activateRegisteredAgentPeers();
      }
    };

    dialog.on("private-chat-message-started", handlers.started);
    dialog.on("private-chat-message-delta", handlers.delta);
    dialog.on("private-chat-message-completed", handlers.completed);
    dialog.on("private-chat-message-error", handlers.error);
    dialog.on(DIALOG_CONNECTION_CONNECTED, handlers.connected);
    this.boundDialog = dialog;
    this.dialogEventHandlers = handlers;
  }

  private unbindDialogPrivateChatEvents(): void {
    if (!this.boundDialog || !this.dialogEventHandlers) {
      return;
    }

    const dialog = this.boundDialog;
    const handlers = this.dialogEventHandlers;
    dialog.off("private-chat-message-started", handlers.started);
    dialog.off("private-chat-message-delta", handlers.delta);
    dialog.off("private-chat-message-completed", handlers.completed);
    dialog.off("private-chat-message-error", handlers.error);
    dialog.off(DIALOG_CONNECTION_CONNECTED, handlers.connected);
    this.boundDialog = null;
    this.dialogEventHandlers = null;
  }

  private markPrivateChatPromptResponded(streamId: string | undefined): PendingPrivateChatPrompt | null {
    if (!streamId) {
      return null;
    }

    const pendingPrompt = this.pendingPrivateChatPrompts.get(streamId);
    if (!pendingPrompt) {
      return null;
    }

    markAgentPeerResponseReceived(pendingPrompt);
    return pendingPrompt;
  }

  private deletePendingPrivateChatPrompt(streamId: string | undefined): PendingPrivateChatPrompt | null {
    if (!streamId) {
      return null;
    }

    const pendingPrompt = this.pendingPrivateChatPrompts.get(streamId);
    if (!pendingPrompt) {
      return null;
    }

    clearAgentPeerResponseWatchdog(pendingPrompt);
    this.pendingPrivateChatPrompts.delete(streamId);
    return pendingPrompt;
  }

  private handlePrivateChatMessageStarted(data: any): void {
    const pendingPrompt = this.markPrivateChatPromptResponded(data?.streamId);

    if (!pendingPrompt) {
      return;
    }

    debugPrivateChatStream("started", {
      streamId: data?.streamId,
      senderSessionId: data?.senderSessionId || "",
      recipientSessionId: data?.recipientSessionId || ""
    });
  }

  private async handlePrivateChatMessageDelta(data: any): Promise<void> {
    const pendingPrompt = this.markPrivateChatPromptResponded(data?.streamId);

    if (!pendingPrompt || isCancelled) {
      return;
    }

    const textDelta = typeof data.textDelta === "string" ? data.textDelta : "";
    if (!textDelta) {
      return;
    }

    pendingPrompt.accumulatedText += textDelta;
    debugPrivateChatStream("delta", {
      streamId: data?.streamId,
      textDelta,
      textDeltaLength: textDelta.length,
      serverAccumulatedText: data?.accumulatedText || "",
      localAccumulatedText: pendingPrompt.accumulatedText
    });
    await pendingPrompt.onMessageChunk({ type: "text", data: textDelta });
  }

  private async handlePrivateChatMessageCompleted(data: any): Promise<void> {
    const pendingPrompt = this.markPrivateChatPromptResponded(data?.streamId);

    if (!pendingPrompt) {
      return;
    }

    const completedText = typeof data.text === "string" ? data.text : "";
    debugPrivateChatStream("completed", {
      streamId: data?.streamId,
      completedText,
      completedTextLength: completedText.length,
      localAccumulatedText: pendingPrompt.accumulatedText,
      localAccumulatedTextLength: pendingPrompt.accumulatedText.length
    });

    if (completedText && completedText !== pendingPrompt.accumulatedText) {
      pendingPrompt.accumulatedText = completedText;
      await pendingPrompt.onCompletedText(completedText);
    }

    this.deletePendingPrivateChatPrompt(data.streamId);
    await pendingPrompt.onEnd();
  }

  private handlePrivateChatMessageError(data: any): void {
    const pendingPrompt = this.markPrivateChatPromptResponded(data?.streamId);

    if (!pendingPrompt) {
      return;
    }

    this.deletePendingPrivateChatPrompt(data.streamId);
    pendingPrompt.onError(data?.error?.message || "Private chat stream failed");
  }

  private async recoverAgentPeerAfterResponseTimeout(streamId: string): Promise<void> {
    const pendingPrompt = this.pendingPrivateChatPrompts.get(streamId);

    if (!pendingPrompt || pendingPrompt.hasAgentResponse) {
      return;
    }

    debugPrivateChatStream("response-timeout", {
      streamId,
      agentId: pendingPrompt.agent.id,
      agentPeerId: pendingPrompt.agent.agentPeerId || ""
    });

    this.deletePendingPrivateChatPrompt(streamId);
    pendingPrompt.onError("The agent did not respond in time. Refreshing the agent connection; please try again.");
    await this.activateAgentPeer(pendingPrompt.agent, { force: true });
  }

  private clearAllPendingPrivateChatPrompts(): void {
    for (const pendingPrompt of this.pendingPrivateChatPrompts.values()) {
      clearAgentPeerResponseWatchdog(pendingPrompt);
    }

    this.pendingPrivateChatPrompts.clear();
  }

  private canUseAgentPeer(agentOrProvider: Agent | string | undefined): boolean {
    const provider = typeof agentOrProvider === "string" ? agentOrProvider : agentOrProvider?.provider;
    return AGENT_PEER_CHAT_PROVIDERS.has(provider || "");
  }

  private getCurrentUserId(): string {
    const authUserId = window.APP?.store?.state?.authUser?.id;
    const profileId = window.APP?.store?.state?.profile?.id;
    const userId = authUserId || profileId;
    if (!userId) {
      console.warn("AIAgents: Could not determine userId. Chat history will be associated with 'ANONYMOUS_USER'.");
      return "ANONYMOUS_USER"; 
    }
    return userId;
  }

  private generateRandomName(): string {
    const now = new Date();
    const timestamp = now.getTime();
    return `Agent_${timestamp}`;
  }

  public generateRandomId(): string {
    const randomPart = Math.random().toString(36).substr(2, 9); // Generate a random alphanumeric string
    const timestamp = Date.now().toString(36); // Convert current timestamp to base36
    return `ID_${timestamp}_${randomPart}`; // Combine timestamp and random part
  }

  public async playAgentAudio(id: string, messageGroup: any, message: any) {
    const agent = this.getAgentById(id);

    if (agent) {
      await this.cancelAllPlay(agent, false);
      if (agent.voiceSpeechStrategy === "WebAPI") {
        const textMessage = getAgentChatAudioText(message);
        await new Promise(resolve => setTimeout(resolve, 100));
        this.playAgentWebAPI(agent, textMessage, message.id, messageGroup.id);

        window.APP.messageDispatch.dispatchEvent(
          new CustomEvent("ai-local-message-edit", {
            detail: {
              type: "private-chat",
              body: { play: false, playable: true, loadingAudio: true },
              messageId: message.id,
              groupId: messageGroup.id
            }
          })
        );
      } else if (agent.voiceSpeechStrategy === "OpenAI") {
        const messageId = getAgentChatAudioMessageId(message);
        const textMessage = getAgentChatAudioText(message);

        if (!messageId || !textMessage) {
          return;
        }

        const cachedAudioBuffer = this.agentChatAudioCache.get(messageId);
        if (!cachedAudioBuffer) {
          window.APP.messageDispatch.dispatchEvent(
            new CustomEvent("ai-local-message-edit", {
              detail: {
                type: "private-chat",
                body: { play: false, playable: true, loadingAudio: true },
                messageId: message.id,
                groupId: messageGroup.id
              }
            })
          );
        }

        try {
          const audioBuffer =
            cachedAudioBuffer ||
            (await this.agentChatAudioCache.getOrCreate(messageId, () =>
              this.audioManager.fetchAudioBlob({ uuid: agent.objectUuid, prompt: textMessage })
            ));
          await this.handleBufferAudio(agent, audioBuffer);

          window.APP.messageDispatch.dispatchEvent(
            new CustomEvent("ai-local-message-edit", {
              detail: {
                type: "private-chat",
                body: { audio: { messageId }, play: true, playable: true, loadingAudio: false },
                messageId: message.id,
                groupId: messageGroup.id
              }
            })
          );
        } catch (error) {
          console.error(`Failed to generate chat audio for message ${messageId}:`, error);
          window.APP.messageDispatch.dispatchEvent(
            new CustomEvent("ai-local-message-edit", {
              detail: {
                type: "private-chat",
                body: { play: false, playable: true, loadingAudio: false },
                messageId: message.id,
                groupId: messageGroup.id
              }
            })
          );
        }
      }
    }
  }

  private async handleAudioUrl(url: string, agent: Agent) {
    this.playAudioUrl(agent, url);
    this.handleAudioEvents(agent);
  }

  private handleAudioEvents(agent: Agent, bufferKey?: number) {
    let audioEl = agent.currentAudio;

    const handlePlaying = () => {
      this.dispatchStartSpeaking(agent);
    };

    // Clean up previous listeners if any
    audioEl.removeEventListener("playing", handlePlaying);
    audioEl.onended = null;

    audioEl.addEventListener("playing", handlePlaying, { once: true });

    const endAudio = () => {
      (window as any).agentDebug = agent;
      this.handleFinishEndAudio(agent);
      audioEl.removeEventListener("playing", handlePlaying);
    };

    audioEl.addEventListener("ended", endAudio, { once: true });
  }

  private async handleBufferAudio(agent: Agent, audioData: any, timestamp?: number, bufferKey?: number) {
    await this.audioManager.playStoredAudio(agent, audioData, timestamp);
    this.handleAudioEvents(agent, bufferKey);
  }

  public playAgentWebAPI(agent: Agent, textMessage: string, messageId?: number, groupId?: string) {
    voiceSynthesizer.agent = agent;
    voiceSynthesizer.addToQueue(textMessage, true, messageId, groupId);
  }

  public sendThreadMessageAI({
    id,
    el,
    message,
    receiver,
    audio = undefined,
    play = undefined,
    loadingAudio = undefined,
    playable = undefined,
    clientMessageId = undefined
  }: {
    id?: any;
    el: any;
    message: any;
    receiver: any;
    audio?: any;
    play?: boolean;
    loadingAudio?: boolean;
    playable?: boolean;
    clientMessageId?: string;
  }) {
    el.dispatchEvent(
      new CustomEvent("ai-message", {
        detail: { type: "private-chat", body: message, receiver, audio, id, play, loadingAudio, playable, clientMessageId }
      })
    );
  }

  public registerAgent(agent: {
    name: string;
    apiKey: string;
    assistantId?: string;
    model?: string;
    endpoint?: string;
    inputType: string;
    encrypted: boolean;
    enableOnProximity: boolean;
    proximityRadius: number;
    el?: HTMLElement;
    id: string;
    objectUuid: string;
    provider: string;
    welcomeText?: string;
    welcomeTextUrl?: string;
    voiceSpeechStrategy: "OpenAI" | "WebAPI" | "Silent";
    systemPrompt?: string;
    persistable?: string;
    targetLanguages?: string[];
    dataSources?: string[];
    enableTranslation?: boolean;
  }): void {
    this.bindDialogPrivateChatEvents();

    const {
      apiKey,
      objectUuid,
      systemPrompt,
      assistantId,
      model,
      endpoint,
      inputType,
      encrypted,
      enableOnProximity,
      proximityRadius,
      el,
      name,
      id,
      welcomeText,
      welcomeTextUrl,
      voiceSpeechStrategy,
      provider,
      persistable,
      targetLanguages,
      dataSources,
      enableTranslation
    } = agent;
    const canUseAgentPeer = this.canUseAgentPeer(provider);
    const agentPeerId = canUseAgentPeer ? getAgentPeerId({ objectUuid }) : undefined;
    this.unregisterAgent(id);

    this.agents.push({
      name: name || this.generateRandomName(),
      id,
      objectUuid,
      apiKey,
      isAi: true,
      assistantId,
      model,
      endpoint,
      inputType,
      encrypted,
      enableOnProximity,
      el,
      proximityRadius,
      welcomeText,
      voiceSpeechStrategy,
      audioBuffers: {},
      welcomeTextUrl,
      currentAudio: new Audio(),
      provider,
      systemPrompt,
      persistable,
      agentPeerId,
      agentPeerStatus: canUseAgentPeer ? "pending" : "disabled",
      agentPeerActivationKey: undefined,
      agentPeerActivationPromise: undefined,
      chatHistory: [],
      targetLanguages,
      dataSources,
      enableTranslation,
    });

    // Dispatch event so UI components can react to agent registration
    window.dispatchEvent(new CustomEvent("ai-agent-registered", { detail: { provider, objectUuid, dataSources } }));

    void this.registerAgentEndpoint(
      objectUuid,
      apiKey,
      assistantId,
      provider,
      model,
      systemPrompt,
      persistable,
      targetLanguages,
      enableTranslation,
      endpoint
    ).then(saved => {
      if (saved) {
        const registeredAgent = this.getAgentById(id);
        if (registeredAgent && this.canUseAgentPeer(registeredAgent)) {
          void this.activateAgentPeerIfSignalingReady(registeredAgent);
        }
      }
    });
  }

  public unregisterAgent(id: string): void {
    this.agents = this.agents.filter(agent => agent.id !== id);

    for (const [streamId, pendingPrompt] of this.pendingPrivateChatPrompts.entries()) {
      if (pendingPrompt.agent.id === id) {
        this.deletePendingPrivateChatPrompt(streamId);
      }
    }
  }

  public clearRoomAgents(): void {
    this.currentAbortController?.abort();
    this.currentAbortController = null;
    this.clearAllPendingPrivateChatPrompts();
    this.agents = [];
  }

  public async setupPersistableInstance(agentId: string): Promise<void> {
    const agent = this.getAgentById(agentId);

    if (agent && agent.persistable && !agent.persistableInstance) {
      try {
        agent.persistableInstance = Persistable.use(agent.persistable);
        // Do not load or write user history here, as userId may not be available yet
      } catch (error) {
        console.error(`Failed to initialize Persistable for agent ${agentId} with key '${agent.persistable}':`, error);
        agent.persistableInstance = undefined;
        agent.chatHistory = [];
      }
    }
  }

  // Removed persistent history read/write. Chat history remains in-memory on the agent.

  private async registerAgentEndpoint(
    uuid: string,
    apiKey: string,
    assistantId: string = "",
    provider: string,
    model: string = "",
    systemPrompt: string = "",
    persistable: string = "",
    targetLanguages?: string[],
    enableTranslation?: boolean,
    endpoint: string = ""
  ): Promise<boolean> {
    const url = `${OPENAI_AGENT_URL}/agent`;
    const authHeaders = (await getAuthHeaders(url, "POST")) as Record<string, string>;

    try {
      const payload: Record<string, any> = {
        uuid: uuid,
        provider: provider,
        assistantId,
        endpoint,
        apiKey: apiKey,
        systemPrompt: systemPrompt,
        memoryType: persistable ? MEMORY_TYPES.persistent : MEMORY_TYPES.temporary,
        targetLanguages: provider === PROVIDERS.OPENAI_TRANSCRIPTION ? targetLanguages : undefined,
        enableTranslation: enableTranslation
      };

      if (model) {
        payload.model = model;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`Failed to register agent ${uuid}: ${response.status}`, errorData);
        return false;
      }

      return true;
    } catch (error) {
      console.error(`Error registering agent ${uuid}:`, error);
      return false;
    }
  }

  private activateRegisteredAgentPeers(): void {
    this.agents.filter(agent => this.canUseAgentPeer(agent)).forEach(agent => {
      void this.activateAgentPeer(agent);
    });
  }

  private isDialogSignalingReady(dialog: any): boolean {
    return Boolean(dialog?._roomId && dialog?._serverUrl && dialog?._protoo?.connected && dialog?._dialogJoined);
  }

  private async activateAgentPeerIfSignalingReady(agent: Agent): Promise<void> {
    this.bindDialogPrivateChatEvents();

    if (!this.canUseAgentPeer(agent)) {
      agent.agentPeerStatus = "disabled";
      return;
    }

    if (!this.isDialogSignalingReady(window.APP?.dialog)) {
      agent.agentPeerStatus = "pending";
      return;
    }

    await this.activateAgentPeer(agent);
  }

  private async activateAgentPeer(agent: Agent, options: { force?: boolean } = {}): Promise<void> {
    this.bindDialogPrivateChatEvents();

    const dialog = window.APP?.dialog;
    const force = options.force === true;

    if (!this.canUseAgentPeer(agent)) {
      agent.agentPeerStatus = "disabled";
      return;
    }

    if (!this.isDialogSignalingReady(dialog)) {
      agent.agentPeerStatus = "pending";
      return;
    }

    try {
      const payload = buildAgentPeerActivationPayload({
        roomId: dialog._roomId,
        dialogUrl: dialog._dialogEndpoint?.serverUrl || dialog._serverUrl,
        objectUuid: agent.objectUuid,
        displayName: agent.name
      });
      const activationKey = getAgentPeerActivationKey(payload);
      agent.agentPeerId = payload.agentPeerId;

      if (!force && agent.agentPeerActivationPromise && agent.agentPeerActivationKey === activationKey) {
        await agent.agentPeerActivationPromise;
        return;
      }

      if (!force && agent.agentPeerStatus === "active" && agent.agentPeerActivationKey === activationKey) {
        return;
      }

      let activationPromise: Promise<void>;
      activationPromise = (async () => {
        agent.agentPeerStatus = "pending";
        agent.agentPeerActivationKey = activationKey;
        const result = await activateAgentPeerRequest(payload);

        if (result?.status === "active" || result?.status === "already-active") {
          agent.agentPeerStatus = "active";
          agent.agentPeerActivationKey = activationKey;
        } else if (result?.status === "disabled") {
          agent.agentPeerStatus = "disabled";
          agent.agentPeerActivationKey = activationKey;
        } else {
          agent.agentPeerStatus = "error";
          agent.agentPeerActivationKey = undefined;
        }
      })();

      agent.agentPeerActivationPromise = activationPromise;
      try {
        await activationPromise;
      } finally {
        if (agent.agentPeerActivationPromise === activationPromise) {
          agent.agentPeerActivationPromise = undefined;
        }
      }
    } catch (error) {
      console.error(`Failed to activate agent peer for agent ${agent.id}:`, error);
      agent.agentPeerStatus = "error";
      agent.agentPeerActivationKey = undefined;
      agent.agentPeerActivationPromise = undefined;
    }
  }

  public updateAgent(agentUpdate: Partial<Agent> & { id: string }) {
    const agentIndex = this.agents.findIndex(a => a.id === agentUpdate.id);
    if (agentIndex !== -1) {
      this.agents[agentIndex] = { ...this.agents[agentIndex], ...agentUpdate };
    }
  }

  public getAgents() {
    return this.agents;
  }

  public getTranslatorAgent(): Agent | null {
    return this.agents.find(a => a.provider === "OpenAI Translator") || null;
  }

  public getAgentById(id: any) {
    const agent = this.agents.find(agent => agent.id == id);
    return agent || null; // Return null if not found
  }

  public getAgentThreadId(id: any): string | null {
    const agent = this.getAgentById(id);
    return agent?.threadId || null;
  }

  public setMyLocalMessageTranscribed({
    messageTranscribed,
    receiver,
    mySessionId
  }: {
    messageTranscribed: any;
    receiver: any;
    mySessionId: any;
  }) {
    window.APP.messageDispatch.dispatchEvent(
      new CustomEvent("ai-local-message", {
        detail: {
          type: "private-chat",
          body: {
            message: messageTranscribed,
            receiver,
            sender: "You",
            sent: true,
            privacyType: "private-chat",
            senderSessionId: mySessionId,
            playable: true
          }
        }
      })
    );
  }

  public async createAgentThread(id: any) {
    const agent = this.getAgentById(id);
    const threadId = await createThread(agent?.apiKey);
    if (agent) {
      agent.threadId = threadId;
    }
  }

  public setAILoadingMessage(receiver: any, clientMessageId?: string) {
    const agent = this.getAgentById(receiver);
    window.APP.messageDispatch.dispatchEvent(
      new CustomEvent("ai-local-message", {
        detail: {
          type: "private-chat",
          body: {
            message: "",
            receiver: receiver,
            sender: agent?.name,
            loading: true,
            privacyType: "private-chat",
            clientMessageId
          }
        }
      })
    );
  }

  public cancelPreviousAudiosPlayStatus() {
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
  }

  public handleErrorTranscription(agent: Agent) {
    this.cancelPreviousAudiosPlayStatus();

    if (agent.voiceSpeechStrategy === "WebAPI") {
      voiceSynthesizer.agent = agent;
      voiceSynthesizer.stop();
      voiceSynthesizer.isSpeaking = false;
      voiceSynthesizer.queue = [];
      voiceSynthesizer.addToQueue(
        "Apologize, There was an unexpected issue processing your audio. Please try again.",
        true
      );
    } else if (agent.voiceSpeechStrategy === "OpenAI") {
      this.handleAudioUrl(this.errorOpenAIAudio, agent);
    }
  }

  public dispatchStartSpeaking(agent?: Agent) {
    if (agent) {
      agent?.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_SPEAK));
    }
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_SPEAK));
  }

  public dispatchAgentTalking(agent: Agent, amplitude: number) {
    if (agent) {
      agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_TALKING_FRAME, { detail: { amplitude } }));
    }

    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_TALKING_FRAME, { detail: { amplitude } }));
  }

  public handleFinishEndAudio(agent: Agent) {
    if (agent) {
      agent?.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-end-audio"));
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
  }

  private unlockSpeechSynthesis() {
    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance("");
      utterance.onend = resolve;
      utterance.onerror = reject;
      window.speechSynthesis.speak(utterance);
    });
  }

  playBlankAudioAll() {
    aiAgents.agents.forEach(agent => {
      if (agent.voiceSpeechStrategy === "WebAPI") {
        this.unlockSpeechSynthesis();
      }
      agent.currentAudio.src = "#";
      agent.currentAudio.autoplay = true;
    });
  }

  public dispatchThinkingAI() {
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_THINKING));
  }

  public dispatchFinishThinkingAI() {
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
  }
  public async speakAndSendPrompt({ agent, message }: { agent: Agent; message: string }) {
    aiAgents.setAILoadingMessage(agent.id);
    await this.sendPrompt({ agent, message, enableSpeech: true });
  }

  public sendPrompt = async ({
    agent,
    message,
    onEndCallback,
    enableSpeech = false,
    messageId,
    turnId
  }: {
    agent: Agent;
    message: string;
    onEndCallback?: (arg: any) => void;
    enableSpeech?: boolean;
    messageId?: number;
    turnId?: string;
  }) => {
    this.bindDialogPrivateChatEvents();
    this.currentPromptId = messageId;
    const aiEntity = agent.el;
    let leftoverText = "";
    let prompt = "";
    let accumulatedAIMessage = "";
    const charactersLimit = 80;
    let isFirstTextChunk = true;

    // Removed persisted history integration. We only use in-memory history.

    this.audioManager.currentAgent = agent;
    this.dispatchThinkingAI();
    agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_THINKING)); // Agent-specific thinking

    const controller = new AbortController();
    this.currentAbortController = controller;
    isCancelled = false;
    let retrying = false;
    this.bufferKey = this.bufferKey + 1;
    const bufferKey = this.bufferKey;
    const promptClientMessageId = turnId ? `${turnId}:user` : `ai-chat:${messageId || Date.now()}:user`;
    const clientMessageId = turnId ? `${turnId}:agent` : `ai-chat:${messageId || Date.now()}`;
    const streamId = `agent-response:${clientMessageId}`;

    const processChunksToSpeech = (chunk: string, messageId?: number) => {
      if (enableSpeech) {
        this.dispatchStartSpeaking(agent);
        voiceSynthesizer.agent = agent;
        voiceSynthesizer.addToQueue(chunk, true, messageId, messageId);
      }
    };

    const onEnd = async () => {
      if (isCancelled) {
        this.currentAbortController = null;
        return;
      }

      isFirstTextChunk = false;

      // Append user and assistant messages only to in-memory chatHistory
      if (message && message.trim().length > 0) {
        agent.chatHistory = [
          ...(agent.chatHistory || []),
          { role: "user", content: message.trim(), timestamp: Date.now() }
        ];
      }
      if (accumulatedAIMessage && accumulatedAIMessage.trim().length > 0) {
        agent.chatHistory = [
          ...(agent.chatHistory || []),
          { role: "assistant", content: accumulatedAIMessage.trim(), timestamp: Date.now() }
        ];
      }

      const autoplay = enableSpeech && typeof document !== 'undefined' && document.hasFocus() && this.currentPromptId === messageId;
      if (agent.voiceSpeechStrategy === "OpenAI" && accumulatedAIMessage.trim().length > 0) {
        const audioText = sanitizeAIMessage(accumulatedAIMessage).trim();
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: audioText,
          receiver: agent.id,
          play: false,
          loadingAudio: true,
          playable: true,
          clientMessageId
        });

        try {
          const audioBuffer = await this.agentChatAudioCache.getOrCreate(clientMessageId, () =>
            this.audioManager.fetchAudioBlob({
              uuid: agent.objectUuid,
              prompt: audioText,
              signal: controller.signal
            })
          );
          if (autoplay) {
            await this.handleBufferAudio(agent, audioBuffer);
          }
          this.audioManager.clearSessionChunks();
          this.sendThreadMessageAI({
            id: messageId,
            el: aiEntity,
            message: audioText,
            receiver: agent.id,
            audio: { messageId: clientMessageId },
            play: autoplay,
            loadingAudio: false,
            playable: true,
            clientMessageId
          });
        } catch (error) {
          console.error(`Failed to eagerly generate chat audio for message ${clientMessageId}:`, error);
          this.sendThreadMessageAI({
            id: messageId,
            el: aiEntity,
            message: audioText,
            receiver: agent.id,
            play: false,
            loadingAudio: false,
            playable: true,
            clientMessageId
          });
        }
      }
      if (leftoverText.trim().length > 0 && agent.voiceSpeechStrategy === "WebAPI" && bufferKey === this.bufferKey) {
        processChunksToSpeech(leftoverText.trim(), messageId);
      }
      if (agent.voiceSpeechStrategy === "WebAPI" && accumulatedAIMessage) {
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: sanitizeAIMessage(accumulatedAIMessage),
          receiver: agent.id,
          audio: true,
          play: false,
          loadingAudio: autoplay,
          playable: true,
          clientMessageId
        });
      } else if (accumulatedAIMessage) {
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: accumulatedAIMessage,
          receiver: agent.id,
          playable: agent.voiceSpeechStrategy !== "Silent",
          clientMessageId
        });
      } else if (!accumulatedAIMessage && !retrying && !isCancelled) {
         this.sendThreadMessageAI({
          id: messageId, el: aiEntity, message: "", receiver: agent.id, clientMessageId
        });
      }

      window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));

      if (onEndCallback) {
        onEndCallback(accumulatedAIMessage);
      }
      this.currentAbortController = null;
    };

    const onMessageChunk = async (chunk: { type: string; data: string }) => {
      if (isCancelled) {
        return;
      }

      if (chunk.type === "text" && chunk.data) {
        if (isFirstTextChunk) {
          if (!enableSpeech || agent.voiceSpeechStrategy === "Silent") {
            this.dispatchFinishThinkingAI();
            if(agent.el) agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
          }
          isFirstTextChunk = false;
        }

        const sanitizedChunkData = sanitizeAIMessage(chunk.data);
        accumulatedAIMessage += sanitizedChunkData;

        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: sanitizeAIMessage(accumulatedAIMessage),
          receiver: agent.id,
          clientMessageId
        });

        const combinedText = leftoverText + sanitizedChunkData;
        const aggregatedChunks = gracefulChunkingWithLimit(combinedText, charactersLimit);
        if (agent.voiceSpeechStrategy === "WebAPI" || agent.voiceSpeechStrategy === "OpenAI") {
            leftoverText += sanitizedChunkData;
        }

      } else if (chunk.type === "thread" && chunk.data) {
        this.updateAgent({ id: agent.id, threadId: chunk.data });
        agent.threadId = chunk.data;
      }
    };

    const onCompletedText = async (text: string) => {
      if (isCancelled) {
        return;
      }

      accumulatedAIMessage = sanitizeAIMessage(text);
      this.sendThreadMessageAI({
        id: messageId,
        el: aiEntity,
        message: accumulatedAIMessage,
        receiver: agent.id,
        clientMessageId
      });
    };

    const onError = (error: string) => {
      if (isCancelled) {
        return;
      }
      console.error(`Error during private chat prompt for agent ${agent.id}:`, error);

      this.dispatchFinishThinkingAI();
      if(agent.el) agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
      isFirstTextChunk = false;

      this.sendThreadMessageAI({
        id: messageId,
        el: aiEntity,
        message: `There was an error processing the prompt. ${error}`,
        receiver: agent.id,
        audio: false,
        play: false,
        loadingAudio: false,
        playable: false,
        clientMessageId
      });
      this.currentAbortController = null;
    };

    this.cancelAllPlay(agent, false);

    if (!this.canUseAgentPeer(agent)) {
      onError("This agent does not accept private chat prompts.");
      return;
    }

    agent.agentPeerId = agent.agentPeerId || getAgentPeerId({ objectUuid: agent.objectUuid });
    debugPrivateChatStream("prompt-start", {
      agentId: agent.id,
      objectUuid: agent.objectUuid,
      agentPeerId: agent.agentPeerId,
      clientMessageId,
      streamId,
      promptClientMessageId,
      text: message
    });
    await this.activateAgentPeer(agent);
    debugPrivateChatStream("prompt-activated", {
      agentId: agent.id,
      agentPeerId: agent.agentPeerId || "",
      agentPeerStatus: agent.agentPeerStatus || "",
      clientMessageId,
      streamId
    });

    this.pendingPrivateChatPrompts.set(streamId, {
      agent,
      messageId,
      accumulatedText: "",
      onMessageChunk,
      onCompletedText,
      onEnd,
      onError
    });
    const pendingPrompt = this.pendingPrivateChatPrompts.get(streamId);

    if (pendingPrompt) {
      startAgentPeerResponseWatchdog(pendingPrompt, {
        streamId,
        onTimeout: ({ streamId: timedOutStreamId }: { streamId: string }) => {
          void this.recoverAgentPeerAfterResponseTimeout(timedOutStreamId);
        }
      });
    }

    controller.signal.addEventListener(
      "abort",
      () => {
        this.deletePendingPrivateChatPrompt(streamId);
      },
      { once: true }
    );

    try {
      const senderUserId = resolveRoomUserId({
        store: window.APP?.store
      });
      const privateChatPayload = buildAgentPrivateChatPayload({
        text: message,
        agentPeerId: agent.agentPeerId,
        clientMessageId: promptClientMessageId,
        responseClientMessageId: clientMessageId,
        persistableId: agent.persistable ? agent.persistableInstance?._uuid : undefined,
        persistableEnv: agent.persistable ? Persistable._env || DEFAULT_PERSISTABLE_ENV : undefined,
        senderUserId
      });

      debugPrivateChatStream("prompt-send-private", {
        streamId,
        clientMessageId,
        promptClientMessageId,
        recipientSessionId: privateChatPayload.recipientSessionId,
        text: privateChatPayload.text
      });
      const acceptedMessage = await window.APP.dialog.sendPrivateChatMessage(privateChatPayload as any);
      debugPrivateChatStream("prompt-send-private-accepted", {
        streamId,
        clientMessageId,
        promptClientMessageId,
        senderSessionId: acceptedMessage?.senderSessionId || "",
        recipientSessionId: acceptedMessage?.recipientSessionId || "",
        text: acceptedMessage?.text || ""
      });
    } catch (error) {
      const pendingPrompt = this.deletePendingPrivateChatPrompt(streamId);
      debugPrivateChatStream("prompt-send-private-error", {
        streamId,
        clientMessageId,
        error: error?.message || String(error)
      });
      if (pendingPrompt) {
        onError(error?.message || "Failed to send private chat prompt");
      }
    }
  };

  public async cancelCurrentPrompt({ id, el }: { id: any; el: any }) {
    const currentAgent = this.getAgentById(id);

    if (this.currentAbortController) {
      isCancelled = true;
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }

    if (currentAgent?.voiceSpeechStrategy === "WebAPI") {
      voiceSynthesizer.queue = [];
      voiceSynthesizer.agent = null;
      voiceSynthesizer.isSpeaking = false;
      voiceSynthesizer.stop();
    }

    const AILastMessage = (window as any).getAILastMessage(id, true);

    if (AILastMessage && AILastMessage.loading) {
      const messageId = AILastMessage.id;
      window.APP.messageDispatch.dispatchEvent(
        new CustomEvent("ai-remove-message", {
          detail: { id: AILastMessage.id, ...AILastMessage }
        })
      );
    }

    const AILastMessageWithoutLoading = (window as any).getAILastMessage(id, false);

    const textMessage = AILastMessageWithoutLoading?.messages[0].body;

    const audio = AILastMessageWithoutLoading?.messages[0]?.audio;

    if (
      AILastMessageWithoutLoading &&
      currentAgent &&
      currentAgent?.voiceSpeechStrategy != "Silent" &&
      AILastMessageWithoutLoading.messages[0] &&
      !audio
    ) {
      this.bufferKey = this.bufferKey + 1;
      const bufferKey = this.bufferKey;

      await this.audioManager.playStreamedAudio({
        agent: currentAgent,
        prompt: textMessage,
        autoplay: false,
        initialAudio: false,
        finalAudio: true,
        bufferKey
      });
      this.audioManager.clearSessionChunks();
      this.sendThreadMessageAI({
        id: AILastMessageWithoutLoading.id,
        el,
        message: sanitizeAIMessage(textMessage),
        receiver: id,
        audio: { bufferKey }
      });
    }

    if (currentAgent && currentAgent.currentAudio && currentAgent.voiceSpeechStrategy === "OpenAI") {
      currentAgent.currentAudio.pause();
      currentAgent.currentAudio.currentTime = 0;

      if (currentAgent.currentAudio.onended) {
        currentAgent.currentAudio.onended(new Event("ended"));
      }
    }

    this.cancelPreviousAudiosPlayStatus();

    this.audioManager.isPlaying = false;
    this.audioManager.clearSessionChunks();
  }

  public async cancelAllPlay(agent: Agent, aiFinishSpeak: any = true) {
    voiceSynthesizer.agent = null;
    voiceSynthesizer.isSpeaking = false;
    voiceSynthesizer.queue = [];
    voiceSynthesizer.stop();
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));

    if (aiFinishSpeak) {
      window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }

    if (!agent) {
      return;
    }

    if (!agent.currentAudio.paused) {
      agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }

    try {
      if (agent && agent.voiceSpeechStrategy === "OpenAI") {
        agent.currentAudio.pause();
      }
    } catch (_) {}
  }

  public async getOpenAITranscriptionByAudio(audioChunks: any, isBuffer: boolean, uuid: string) {
    let audioBlob;
    if (isBuffer) {
      audioBlob = new Blob([audioChunks], { type: "audio/mp3" });
    } else {
      audioBlob = new Blob(audioChunks, { type: "audio/mp3" });
    }

    try {
      const promptTranscribed = await sendAudioTranscription({
        audio: audioBlob,
        signal: this.currentAbortController?.signal,
        uuid
      });
      return promptTranscribed;
    } catch (error) {
      console.error("Error in transcription", error);
      return null;
    }
  }

  private playAudioUrl(agent: Agent, url: string) {
    agent.currentAudio.src = url;
    agent.currentAudio.play();
  }
}

const aiAgents = AIAgents.getInstance();
declare global {
  interface Window {
    aiAgents: any;
  }
}

window.aiAgents = aiAgents;

export default aiAgents;
